import { useContext, useEffect, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import LoadingOverlayWrapper from '../../app/components/LoadingOverlayWrapper';
import ProductUpdatesDateFilter from './filters/ProductUpdatesDateFilter';
import ProductUpdatesSearch from './filters/ProductUpdatesSearch';
import RolloutProductUpdateStatusTag from './RolloutProductUpdateStatusTag';
import RolloutProductUpdateProducts from './RolloutProductUpdateProducts';
import RolloutProductUpdateDate from './RolloutProductUpdateDate';
import { FilterBar } from 'foundations-components/transitional/components/FilterBar';
import { Table } from 'foundations-components/transitional/components/Table';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { useSetQueryParams } from '../../app/hooks/useSetQueryParams';
import { useRawParamsForListRolloutProductUpdates } from '../hooks/useRawParamsForListRolloutProductUpdates';
import { EXCLUDED_FILTERS, EXCLUDED_FILTERS_NEW_TO_YOU, EXCLUDED_FILTERS_RECOMMENDED, EXCLUDED_FILTERS_SCHEDULED_FOR_RELEASE, EXCLUDED_FILTERS_YOUR_BETAS, getRequestParamsForGetRolloutProductUpdates, getTrackingSubscreenTab, productToTranslationKey } from '../utils';
import MultiSelectFilter from './filters/MultiSelectFilter';
import { CustomerFacingProducts, InternalFacingProducts, Products } from '../ProductTypes';
import ProductUpdatesTablePaginator from './ProductUpdatesTablePaginator';
import NoResultsMessage from './NoResultsMessage';
import { useDateFieldForCategory } from '../hooks/useDateFieldForCategory';
import RolloutProductUpdatesScheduledForReleaseTag from './RolloutProductUpdatesScheduledForReleaseTag';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TrackingAction, TrackingFeature, TrackingScreen, trackUsage } from '../../app/utils/tracking';
import { momentToSimpleDate, utcToRolloutOffsetLocal } from '../../app/utils/dateUtils';
import { useAppSelector } from '../../app/indexReducer';
import { BulbIcon, FavoriteIcon } from 'foundations-assets';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { getFeatureReleaseUrl } from '../../app/utils/urls';
import { selectHasSlowRolloutScope } from '../../app/auth/ScopeSelectors';
import PortalIdParser from 'PortalIdParser';
import RolloutProductUpdateImpactTag from './RolloutProductUpdateImpactTag';
import InViewTrigger from './utils/InViewTrigger';
import { Badge } from 'foundations-components/transitional/components/Badge';
import I18n from 'I18n';
import Header from './ProductUpdatesHeader';
import { selectAuthenticatedUser } from '../../app/auth/AuthSelectors';
import AllCaughtUpMessage from './AllCaughtUpMessage';
import { addErrorAlert } from '../../app/utils/alerting';
import Raven from 'raven-js';
import { validatePUData } from './utils/validatePUData';
import ProductUpdatesCalendar from './ProductUpdatesCalendar';
import { ScheduledUpdatesViews } from '../constants';
import ProductUpdatesBetaButton from './ProductUpdatesBetaButton';
import ProductUpdatesCalendarHeader from './ProductUpdatesCalendarHeader';
import { useReactiveVar } from '@apollo/client';
import { productUpdatesReactiveVar } from '../../productUpdatesReactiveVar';
import useEmailTracker from '../hooks/useEmailTracker';
import { useLocation } from 'react-router-dom';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import useFetchProductUpdates from '../hooks/useFetchProductUpdates';
import handleError from '../../app/utils/handleError';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Link } from 'foundations-components/transitional/components/Link';
import { createLoggedUnknownProductsSessionStorage } from '../data/local-data/storeProvider';
import FeedbackButton from './FeedbackButton';
import { selectHasNewToYouGate } from '../../app/auth/GateSelectors';
import { FeedbackBtnClickTrackerContext, MeetingScheduleLinkClickTrackerContext } from '../contexts/Tracking';
import { categoryToSubscreenMap } from '../../app/utils/tracking/utils';
import { NewToYouContext } from './IndexPage';
import { AllFeatureGroupsContext } from './IndexPage';
import { GATES } from '../../app/utils/constants';
import GateWrapper from './utils/GateWrapper';
import FavoriteFilter from './filters/FavoriteFilter';
import { isTrellisTheme, useTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const isLegitError = error => error && (error.networkError === null ||
// networkError type does not contain errorMessage
error.networkError.errorMessage !== 'Request aborted');
const ProductUpdatesCategoryView = ({
  getAllPUError,
  isDBLoading,
  newToYouProductUpdates,
  newToYouTotalCount,
  processAllScheduledViewedPUs,
  schedulePUViewed,
  startStageDate,
  unviewedPUIds
}) => {
  const theme = useTheme();
  const isTrellis = isTrellisTheme(theme);
  const btnUseSecondary = isTrellis ? 'secondary' : 'tertiary-light';
  const setQueryParams = useSetQueryParams();
  const location = useLocation();
  const hasNewToYouGate = useAppSelector(selectHasNewToYouGate);
  const rawParams = useRawParamsForListRolloutProductUpdates();
  const paramsForGetRolloutProductUpdates = getRequestParamsForGetRolloutProductUpdates(rawParams);
  const hasSlowRolloutScope = useAppSelector(selectHasSlowRolloutScope);
  const portalId = PortalIdParser.get();
  const user = useAppSelector(selectAuthenticatedUser);
  const userId = user && user.user_id;
  const {
    category,
    limit,
    offset,
    rolloutState: rolloutStateFromParams,
    view
  } = rawParams;
  useEmailTracker({
    portalId,
    userId
  });
  const dateField = useDateFieldForCategory();
  const [cacheUpdated, setCacheUpdated] = useState(false);
  const {
    newToYouTimestamp
  } = useContext(NewToYouContext);
  const {
    data,
    error: getPUError,
    loading
  } = useFetchProductUpdates({
    category,
    paramsForGetRolloutProductUpdates,
    portalId,
    setCacheUpdated,
    userId,
    validatePUData
  });
  const {
    allFeatureGroups,
    isLoadingFeatureGroups
  } = useContext(AllFeatureGroupsContext);
  const featureGroupFilterOptions = useMemo(() => allFeatureGroups ? Array.from(allFeatureGroups.values()).sort((a, b) => a.displayName.localeCompare(b.displayName)).map(fg => ({
    text: fg.displayName,
    value: fg.name
  })) : [], [allFeatureGroups]);
  useEffect(() => {
    setCacheUpdated(false);
  }, [data]);
  useEffect(() => {
    if (getPUError || getAllPUError) {
      addErrorAlert('app.errors.generic');
      if (isLegitError(getPUError)) {
        Raven.captureMessage('Failed to fetch product updates', {
          extra: {
            userId,
            portalId,
            error: getPUError
          }
        });
      }
      if (isLegitError(getAllPUError)) {
        Raven.captureMessage('Failed to fetch all product updates', {
          extra: {
            userId,
            portalId,
            error: getPUError
          }
        });
      }
    }
  }, [userId, portalId, getPUError, getAllPUError]);
  const {
    rolloutProductUpdates: _rolloutProductUpdates,
    totalCount: _totalCount
  } = data ? data.rolloutProductUpdates : {
    rolloutProductUpdates: [],
    totalCount: 0
  };
  const isNewToYouTab = category === 'new-to-you';
  const isRecommendedTab = category === 'recommended';
  const isScheduledForReleaseTab = category === 'scheduled-for-release';
  const rolloutProductUpdates = hasNewToYouGate ? _rolloutProductUpdates : isNewToYouTab ? newToYouProductUpdates : _rolloutProductUpdates;
  const totalCount = hasNewToYouGate ? _totalCount : isNewToYouTab ? newToYouTotalCount : _totalCount;
  const showBetaStatusFilter = category && category === 'in-beta';
  const {
    checkUnknownProductLogSent,
    setUnknownProductLogSent
  } = useMemo(() => createLoggedUnknownProductsSessionStorage(), []);
  useEffect(() => {
    if (rolloutProductUpdates.length > 0) {
      const unknownProducts = new Set(rolloutProductUpdates.map(({
        rollout: {
          products
        }
      }) => products).flat().filter(product => !CustomerFacingProducts.has(product) && !InternalFacingProducts.has(product)));
      const unLoggedUnknownProducts = Array.from(unknownProducts).filter(product => !checkUnknownProductLogSent(product));
      unLoggedUnknownProducts.forEach(unloggedUnknownProduct => {
        setUnknownProductLogSent(unloggedUnknownProduct);
      });
      if (unLoggedUnknownProducts.length > 0) {
        handleError(new Error(`Unknown products: ${unLoggedUnknownProducts.join(', ')}`));
      }
    }
  }, [rolloutProductUpdates, checkUnknownProductLogSent, setUnknownProductLogSent]);
  const excludedFilters = useMemo(() => {
    switch (category) {
      case 'scheduled-for-release':
        return new Set(EXCLUDED_FILTERS_SCHEDULED_FOR_RELEASE);
      case 'recommended':
        return new Set(EXCLUDED_FILTERS_RECOMMENDED);
        break;
      case 'your-betas':
        return new Set(EXCLUDED_FILTERS_YOUR_BETAS);
      case 'new-to-you':
        return hasNewToYouGate ? new Set(EXCLUDED_FILTERS_NEW_TO_YOU) : new Set(EXCLUDED_FILTERS);
      default:
        return new Set(EXCLUDED_FILTERS);
    }
  }, [category, hasNewToYouGate]);
  const hasFilters = Object.keys(paramsForGetRolloutProductUpdates).filter(key => !excludedFilters.has(key)).some(key => paramsForGetRolloutProductUpdates[key] !== null);
  useEffect(() => {
    if (isNewToYouTab && processAllScheduledViewedPUs) {
      processAllScheduledViewedPUs().catch(e => {
        handleError(e, {
          description: 'processAllScheduledViewedPUs failed in',
          errors: e
        });
      });
    }
  }, [isNewToYouTab, processAllScheduledViewedPUs]);
  const productUpdatesCache = useReactiveVar(productUpdatesReactiveVar);
  const rows = rolloutProductUpdates.reduce((prev, pu) => {
    const puData = productUpdatesCache.get(`${pu.id}`);
    if (puData !== undefined) {
      return [...prev, puData];
    }
    return prev;
  }, []).map(productUpdate => {
    const {
      featureGroups,
      id,
      recommended,
      releaseDate,
      rollout: {
        enrollType,
        enrollmentStatus,
        gate,
        id: rolloutId,
        impactLevel,
        products,
        betaPaused
      },
      isFavorite,
      rolloutState,
      stageDate,
      title,
      untranslatedTitle,
      updatedAt
    } = productUpdate;
    const thirtyDaysAgo = utcToRolloutOffsetLocal(I18n.moment()).subtract(30, 'days');
    const isLiveWithinThirtyDays = rolloutState === 'LIVE' && releaseDate && utcToRolloutOffsetLocal(releaseDate).isAfter(thirtyDaysAgo);
    const showLiveFeedbackButton = isLiveWithinThirtyDays;
    const handleProductUpdateSelect = () => {
      setQueryParams({
        rollout: rolloutId.toString(),
        update: ''
      });
    };
    const wrapWithInViewTrigger = node => unviewedPUIds.has(id) ? /*#__PURE__*/_jsx(InViewTrigger, {
      onVisibilityChange: isVisible => {
        if (isVisible) {
          schedulePUViewed(id);
        }
      },
      children: node
    }, id) : node;
    return /*#__PURE__*/_jsxs("tr", {
      "data-test-id": "product-update-row",
      children: [/*#__PURE__*/_jsx("td", {
        children: wrapWithInViewTrigger( /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "100",
            children: [/*#__PURE__*/_jsx(Link, {
              "data-test-id": "product-update-title",
              onClick: handleProductUpdateSelect,
              children: /*#__PURE__*/_jsx(TruncateString, {
                matchContentWidth: true,
                "data-test-id": "product-update-title-text",
                children: title
              })
            }), /*#__PURE__*/_jsx(GateWrapper, {
              ungatedToAny: [GATES.NEW_TO_YOU_GATE],
              fallbackComponent: unviewedPUIds.has(id) && !isNewToYouTab && /*#__PURE__*/_jsx(Badge, {
                use: "new",
                children: updatedAt === stageDate ? /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "app.badges.updated"
                }) : /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "app.badges.new"
                })
              }),
              children: newToYouTimestamp && stageDate >= newToYouTimestamp && !isNewToYouTab && /*#__PURE__*/_jsx(Badge, {
                use: "new",
                children: updatedAt === stageDate ? /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "app.badges.updated"
                }) : /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "app.badges.new"
                })
              })
            }), recommended && !isRecommendedTab && /*#__PURE__*/_jsx(Tooltip, {
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.sidebar.recommendedIconTooltip"
              }),
              children: /*#__PURE__*/_jsx(IconHolder, {
                size: "200",
                icon: BulbIcon,
                color: "do-not-use-eerie",
                padding: "0"
              })
            }), /*#__PURE__*/_jsx(GateWrapper, {
              ungatedToAny: [GATES.FAVORITE_PU_GATE],
              children: isFavorite && /*#__PURE__*/_jsx(IconHolder, {
                size: "200",
                icon: FavoriteIcon,
                color: "icon-core-subtle",
                padding: "0"
              })
            })]
          }), /*#__PURE__*/_jsx(RolloutProductUpdateStatusTag, {
            id: id,
            rolloutId: rolloutId,
            title: title,
            untranslatedTitle: untranslatedTitle,
            gate: gate,
            rolloutState: rolloutState,
            enrollmentStatus: enrollmentStatus,
            enrollType: enrollType,
            betaPaused: betaPaused
          }), /*#__PURE__*/_jsx(RolloutProductUpdatesScheduledForReleaseTag, {
            date: releaseDate,
            rolloutState: rolloutState
          })]
        }))
      }), /*#__PURE__*/_jsx("td", {
        children: /*#__PURE__*/_jsx(RolloutProductUpdateImpactTag, {
          impactLevel: impactLevel
        })
      }), /*#__PURE__*/_jsx("td", {
        children: /*#__PURE__*/_jsx(RolloutProductUpdateProducts, {
          rolloutProducts: products,
          featureGroups: featureGroups
        })
      }), /*#__PURE__*/_jsx("td", {
        children: /*#__PURE__*/_jsx(RolloutProductUpdateDate, {
          date: dateField === 'stageDate' ? stageDate : releaseDate ? utcToRolloutOffsetLocal(releaseDate).valueOf() : rolloutState === 'LIVE' || rolloutState === 'SUNSET' ? stageDate // default to stageDate if no releaseDate for LIVE or SUNSET
          : null
        })
      }), /*#__PURE__*/_jsx("td", {
        children: showLiveFeedbackButton ? /*#__PURE__*/_jsx(FeedbackBtnClickTrackerContext.Provider, {
          value: {
            screen: TrackingScreen.PRODUCT_UPDATES,
            subscreen: categoryToSubscreenMap[category !== null && category !== void 0 ? category : 'all'],
            origin: 'table',
            rolloutState: 'LIVE',
            untranslatedPUTitle: untranslatedTitle
          },
          children: /*#__PURE__*/_jsx(MeetingScheduleLinkClickTrackerContext.Provider, {
            value: {
              screen: TrackingScreen.PRODUCT_UPDATES,
              subscreen: categoryToSubscreenMap[category !== null && category !== void 0 ? category : 'all'],
              origin: 'table'
            },
            children: /*#__PURE__*/_jsx(FeedbackButton, {
              use: btnUseSecondary,
              size: "small",
              rolloutId: rolloutId,
              productUpdateId: id,
              productUpdateTitle: title
            })
          })
        }) : /*#__PURE__*/_jsx(ProductUpdatesBetaButton, {
          productUpdate: productUpdate,
          category: category
        })
      })]
    }, id);
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Header, {
      portalId: portalId,
      category: category
    }), !isLoadingFeatureGroups && /*#__PURE__*/_jsx(FilterBar, {
      startSlot: /*#__PURE__*/_jsxs(_Fragment, {
        children: [category && /*#__PURE__*/_jsx(ProductUpdatesSearch, {
          category: category
        }), /*#__PURE__*/_jsx(MultiSelectFilter, {
          category: category,
          filterType: "impactLevels",
          menuInlineSize: 150,
          optionTextValue: RolloutProductUpdateImpactTag.FilterOptions,
          translationKey: "app.filters.impactLevels.options",
          onChangeCB: () => {
            trackUsage(TrackingScreen.PRODUCT_UPDATES, getTrackingSubscreenTab(category), TrackingFeature.IMPACT_LEVELS_FILTER, TrackingAction.CLICK);
          }
        }), /*#__PURE__*/_jsx(MultiSelectFilter, {
          disabled: isLoadingFeatureGroups,
          category: category,
          filterType: "app",
          menuInlineSize: 300,
          optionTextValue: featureGroupFilterOptions
        }), /*#__PURE__*/_jsx(MultiSelectFilter, {
          category: category,
          filterType: "product",
          menuInlineSize: 300,
          optionTextValue: ['hubspot-free', ...Products.filter(p => !p.includes('-free'))].map(p => ({
            translationKey: productToTranslationKey(p),
            value: p
          })).sort((a, b) => a.translationKey.localeCompare(b.translationKey)),
          translationKey: "app.products"
        }), showBetaStatusFilter && /*#__PURE__*/_jsx(MultiSelectFilter, {
          category: category,
          filterType: "betaStatus",
          menuInlineSize: 150,
          optionTextValue: ['pending', 'enrolled', 'not-enrolled'],
          translationKey: "app.filters.betaStatus.options"
        }), isRecommendedTab && /*#__PURE__*/_jsx(MultiSelectFilter, {
          category: category,
          filterType: "rolloutState",
          menuInlineSize: 175,
          optionTextValue: ['live', 'in-beta', 'in-development', 'sunset'],
          translationKey: "app.statuses"
        }), /*#__PURE__*/_jsx(GateWrapper, {
          ungatedToAny: [GATES.FAVORITE_PU_GATE],
          children: !isNewToYouTab && /*#__PURE__*/_jsx(FavoriteFilter, {})
        }), hasFilters && /*#__PURE__*/_jsx(View, {
          minInlineSize: "100px",
          children: /*#__PURE__*/_jsx(RouterLink, {
            to: `${location.pathname}`,
            children: I18n.text('app.filters.clear')
          })
        })]
      }),
      endSlot: /*#__PURE__*/_jsx(GateWrapper, {
        ungatedToAny: [GATES.NEW_TO_YOU_GATE],
        fallbackComponent: /*#__PURE__*/_jsx(ProductUpdatesDateFilter, {
          min: isNewToYouTab ? momentToSimpleDate(I18n.moment(startStageDate)) : undefined
        }),
        children: null
      })
    }), isScheduledForReleaseTab && /*#__PURE__*/_jsx(ProductUpdatesCalendarHeader, {}), /*#__PURE__*/_jsx("div", {
      style: {
        position: 'relative'
      },
      children: isLegitError(getPUError) || isLegitError(getAllPUError) ? /*#__PURE__*/_jsx(ErrorMessage, {
        type: "badRequest",
        children: /*#__PURE__*/_jsx("p", {
          children: I18n.text('app.errors.loadFailed')
        })
      }) : /*#__PURE__*/_jsx(LoadingOverlayWrapper, {
        loading: loading || isDBLoading || isLoadingFeatureGroups,
        children: cacheUpdated && rows.length === 0 && view !== ScheduledUpdatesViews.CALENDAR || isRecommendedTab && rolloutStateFromParams === 'none' ? hasFilters ?
        /*#__PURE__*/
        // FIXME: should only mount this component when there are 'customer set' filters
        _jsx(NoResultsMessage, {
          category: category
        }) : /*#__PURE__*/_jsx(AllCaughtUpMessage, {
          hasFirstPage: totalCount > 0,
          isRecommendedTab: isRecommendedTab
        }) : isScheduledForReleaseTab && view === ScheduledUpdatesViews.CALENDAR ? /*#__PURE__*/_jsx(ProductUpdatesCalendar, {
          rolloutProductUpdates: rolloutProductUpdates
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(Table, {
            children: [/*#__PURE__*/_jsx("colgroup", {
              children: /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx("col", {
                  width: "34%"
                }), /*#__PURE__*/_jsx("col", {
                  width: "18%"
                }), /*#__PURE__*/_jsx("col", {
                  width: "20"
                }), /*#__PURE__*/_jsx("col", {
                  width: "20"
                }), /*#__PURE__*/_jsx("col", {
                  width: "8%"
                })]
              })
            }), /*#__PURE__*/_jsx("thead", {
              children: /*#__PURE__*/_jsxs("tr", {
                children: [/*#__PURE__*/_jsx("th", {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.columns.update"
                  })
                }), /*#__PURE__*/_jsx("th", {
                  children: /*#__PURE__*/_jsxs(Flex, {
                    children: [/*#__PURE__*/_jsx(FormattedMessage, {
                      message: "app.columns.impact"
                    }), /*#__PURE__*/_jsx(HelpIcon, {
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "app.columns.impactTooltip"
                      }),
                      tooltipPlacement: "bottom",
                      size: "200",
                      marginInlineStart: "100",
                      color: "icon-core-subtle"
                    })]
                  })
                }), /*#__PURE__*/_jsx("th", {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.columns.products"
                  })
                }), /*#__PURE__*/_jsx("th", {
                  children: /*#__PURE__*/_jsxs(Flex, {
                    children: [/*#__PURE__*/_jsx(FormattedMessage, {
                      message: `app.columns.${dateField}`
                    }), isScheduledForReleaseTab && hasSlowRolloutScope && /*#__PURE__*/_jsx(HelpIcon, {
                      title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                        message: "app.columns.releaseDateTooltip_jsx",
                        options: {
                          href: getFeatureReleaseUrl()
                        },
                        elements: {
                          Link
                        }
                      }),
                      tooltipPlacement: "bottom",
                      size: "200",
                      marginInlineStart: "100",
                      color: "icon-core-subtle"
                    })]
                  })
                }), /*#__PURE__*/_jsx("th", {})]
              })
            }), /*#__PURE__*/_jsx("tbody", {
              children: rows
            })]
          }), /*#__PURE__*/_jsx(ProductUpdatesTablePaginator, {
            limit: limit || 10,
            offset: offset || 0,
            totalCount: totalCount
          })]
        })
      })
    })]
  });
};
export default ProductUpdatesCategoryView;